/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ProductsContext, defaultValue, useColorMode } from "@lib/provider";
import { Product } from "@types";
import { useCallback, useContext } from "react";

export const useProducts = () => {
    const { products, refetch, isLoading } = useContext(ProductsContext);
    const { product: productType } = useColorMode();

    const getFilteredProducts = useCallback(
        (free = true) => {
            return products.filter((p) => {
                if (!free && p.is_free) {
                    return false;
                }

                return p.id.includes(productType);
            });
        },
        [productType, products]
    );

    const getDefaultProduct = useCallback(
        (inactive = false, paid = false) => {
            const filtered = getFilteredProducts();

            if (paid && filtered.length) {
                return filtered.filter((p) => p.is_individual)?.[0] ?? filtered[0];
            }

            if (filtered.length && !inactive) {
                return filtered[0];
            }

            return defaultValue.products[0];
        },
        [getFilteredProducts]
    );

    const getProductFromId = useCallback(
        (id: string) => {
            return products.find((p) => p.id === id) ?? getDefaultProduct();
        },
        [products, getDefaultProduct]
    );
    const getProductFromAllowedWorkers = useCallback(
        (allowed_workers: number) => {
            return products.find((p) => p.allowed_workers === allowed_workers) ?? getDefaultProduct();
        },
        [products, getDefaultProduct]
    );
    const getShortName = useCallback((product: Product) => {
        const [_, type] = product.name.split(" ");

        return type;
    }, []);

    const getSortedProducts = useCallback(
        (paid = true, isFree = false) => {
            return products
                .filter((p) => (paid && p.is_individual) || (isFree && p.is_free))
                .sort((a, b) => {
                    const productA = a.id.includes(productType);
                    const productB = b.id.includes(productType);

                    if (productA && !productB) {
                        return -1; // a should come before b
                    } else if (!productA && productB) {
                        return 1; // b should come before a
                    } else {
                        return 0; // maintain the original order
                    }
                });
        },
        [productType, products]
    );

    return {
        products,
        getShortName,
        getProductFromAllowedWorkers,
        getProductFromId,
        refetch,
        isLoadingProducts: isLoading,
        getDefaultProduct,
        getFilteredProducts,
        getSortedProducts,
    };
};
